/**
 * Les chemins de l’application, écrits une seule fois (ARCHITECTURE §9.1).
 *
 * Trois espaces dans un seul artefact :
 *   `/admin/*`   back-office (session staff, rôle selon l’écran)
 *   `/scan/*`    contrôle d’accès (session staff, guichet ou admin)
 *   `/portail/*` abonné, lecture seule (session abonné)
 *   `/`          redirige selon la session
 */

import type { RoleUtilisateur } from '@tb/shared'

export const CHEMINS = {
  racine: '/',

  /* Boutique publique (BOU-01…BOU-10) ------------------------------------ */
  /**
   * ★ Le QUATRIÈME espace, et le seul sans garde : « aucun compte à créer pour
   * acheter » (BOU-10). Ce n’est pas un oubli d’authentification, c’est la
   * fonctionnalité — un visiteur qui doit s’inscrire pour acheter deux places
   * n’achète pas.
   */
  boutique: '/boutique',
  boutiqueAbonnements: '/boutique/abonnements',
  boutiquePanier: '/boutique/panier',
  boutiqueCoordonnees: '/boutique/coordonnees',
  boutiqueRecapitulatif: '/boutique/recapitulatif',

  /* Back-office ---------------------------------------------------------- */
  admin: '/admin',
  connexionStaff: '/admin/connexion',
  configuration: '/admin/configuration',
  catalogue: '/admin/catalogue',
  incidents: '/admin/incidents',
  tarification: '/admin/tarification',
  formules: '/admin/formules',
  campagne: '/admin/campagne',
  souscriptions: '/admin/souscriptions',
  ventes: '/admin/ventes',
  /** BOU-11…BOU-13 — vente téléphonique : même parcours que la boutique. */
  paniers: '/admin/paniers',
  paniersOuverts: '/admin/paniers/ouverts',
  paiements: '/admin/paiements',
  caisse: '/admin/paiements/caisse',
  rapprochement: '/admin/paiements/rapprochement',
  comptabilite: '/admin/paiements/comptabilite',
  imports: '/admin/imports',
  tableaux: '/admin/tableaux',

  /* Contrôle d’accès ----------------------------------------------------- */
  scan: '/scan',

  /* Portail abonné ------------------------------------------------------- */
  portail: '/portail',
  connexionAbonne: '/portail/connexion',
  verificationLienMagique: '/portail/connexion/verifier',

  /* Démonstration du plan de salle (développement uniquement) ------------- */
  demoPlan: '/demo/plan',
} as const

/** Page d’un spectacle dans la boutique (BOU-02). */
export function cheminSpectacleBoutique(spectacleId: string): string {
  return `/boutique/spectacles/${spectacleId}`
}

/** Choix des places pour une soirée (BOU-03). */
export function cheminPlacesBoutique(representationId: string): string {
  return `/boutique/representations/${representationId}`
}

/**
 * PAI-01 — l’écran de paiement d’une commande.
 *
 * ★ Le jeton porteur n’y figure PAS : une URL voyage dans l’historique du
 * navigateur, les journaux du proxy et l’en-tête `Referer`. Il vit dans le
 * `sessionStorage` de l’onglet (`hooks-paiement.ts`). C’est aussi l’adresse que
 * le prestataire simulé rend comme `urlPaiement` — le jour où un vrai
 * prestataire est branché, cette page devient sa page de retour.
 */
export function cheminPaiementBoutique(paiementId: string): string {
  return `/boutique/paiement/${paiementId}`
}

/**
 * Écran d’accueil d’un membre du personnel, selon son métier :
 * l’administratrice arrive sur les chiffres, le guichet sur la vente, la
 * direction sur les tableaux de bord.
 */
export function accueilStaff(role: RoleUtilisateur): string {
  if (role === 'partenaire') return '/admin/partenaire'
  if (role === 'controle') return CHEMINS.scan
  if (role === 'guichet') return CHEMINS.ventes
  return CHEMINS.tableaux
}

/** Paramètre de retour après connexion : `/admin/connexion?suite=/admin/ventes`. */
export const PARAM_SUITE = 'suite'

/** Construit l’URL de connexion en mémorisant la page demandée. */
export function urlConnexion(base: string, destination: string): string {
  if (destination === '' || destination === '/') return base
  return `${base}?${PARAM_SUITE}=${encodeURIComponent(destination)}`
}

/**
 * Lit la destination de retour. On n’accepte qu’un chemin **interne**
 * (commençant par `/` sans `//`) : une redirection ouverte serait un vecteur
 * d’hameçonnage sur un écran de connexion.
 */
export function lireSuite(parametres: URLSearchParams, defaut: string): string {
  const suite = parametres.get(PARAM_SUITE)
  if (suite === null) return defaut
  if (!suite.startsWith('/') || suite.startsWith('//')) return defaut
  return suite
}
